/**
 * Concrete implementation of Hicom slider
 */
class HicomSlider implements IHicomSlider {

    /**
     * The type of slider. This can be either a standard slider or a range slider
     */
    public static readonly SLIDER_TYPE = {
        Standard: 'standard',
        Range: 'range'
    }

    /**
    * Represents the orientation of the HicomSlider.
    * This can be used to determine the layout direction of the slider.
    */
    public static readonly ORIENTATION = {
        Horizontal: 'horizontal',
        Vertical: 'vertical'
    }

    /**
     * The default width of the slider in percentage. 
     * This is used to resize the slider.
     */
    private static readonly DEFAULT_SLIDER_WIDTH = 40;

    /**
     * The default height of the slider in pixels. 
     * This is used to resize the slider.
     */
    private static readonly DEFAULT_SLIDER_HEIGHT = 400;

    /**
     * Kendo slider instance. This can be either a standard slider or a range slider
     */
    private _slider: kendo.ui.Slider | kendo.ui.RangeSlider;

    /**
     * The type of slider. This can be either a standard slider or a range slider
     */
    private _sliderType: string;

    /**
     * The orientation of the slider
     */
    private _orientation: string;

    /**
     * The slider is read only
     */
    private _isReadOnly: Boolean;

    /**
     * The slider is disabled
     */
    private _isDisabled: Boolean;

    /**
     * The id of the slider
     */
    private _id: string;

    /**
     * Overloaded signatures for the constructor
     * @param standardSlider - Kendo standard slider
     * @param rangeSlider - Kendo range slider
     */
    constructor(standardSlider: kendo.ui.Slider);
    constructor(rangeSlider: kendo.ui.RangeSlider);

    /**
     * The constructor for the HicomSlider. This can be either a standard slider or a range slider
     * @param slider
     */
    constructor(slider: kendo.ui.Slider | kendo.ui.RangeSlider) {
        this._slider = slider;
        this._id = slider.element[0].getAttribute("id");
        this._sliderType = slider.element[0].getAttribute("data-slider-type");
        this._orientation = slider.element[0].getAttribute("data-orientation");
        this._isReadOnly = this._slider.element.hasClass('hicom-readonly');
        this._isDisabled = this._slider.element.hasClass('hicom-disabled')
        this._init();
    }

    /**
     * Initialise the slider
     */
    private _init() {

        // Get the parent form element of the slider and create a new HicomForm instance with the form's id
        let form = this._slider.element.parents('form');
        let hicomForm = new HicomForm(form.attr('id'));

        // Set the original value of the range slider
        if (this._sliderType === HicomSlider.SLIDER_TYPE.Range) {
            this._slider.element.attr('data-df-orig', this._slider.value());
        }

        // Set slider to readonly
        if (this._isReadOnly) {
            hicomForm.SetReadOnly([this._id]);
        }

        // Set slider to disabled
        if (this._isDisabled) {
            hicomForm.SetDisabled([this._id]);
        }

        // Bind the change event to set the dirty status of the slider
        this._slider.bind("change", (e) => {
            let $element = $(e.sender.element);
            let value = $element.data('slider-type') === HicomSlider.SLIDER_TYPE.Standard ? $element.val() : `${e.value[0]},${e.value[1]}`;
            hicomForm.SetDirty();
            HicomDirtyForms.setDirtyStatus($element, value);
        });
    }

    /**
     * Resize the slider
     * @param value - The value to resize the slider to.
     * @param unit - The unit of the value. Defaults to '%'. Other units can be used such as 'px'
     * @param orientation - The orientation of the slider. Defaults to 'Horizontal'.
     */
    public resize(value: Number, unit: string = '%', orientation: string = HicomSlider.ORIENTATION.Horizontal) {
        switch (orientation) {
            case HicomSlider.ORIENTATION.Horizontal:
                this._slider.wrapper.css("width", `${value}${unit}`);
                this._slider.resize();
                break;
            case HicomSlider.ORIENTATION.Vertical:
                this._slider.wrapper.css("height", `${value}${unit}`);
                this._slider.resize();
                break;
            default:
                break;
        }
    }
}